import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router';
import { formatCents } from '@crave/shared';
import { ApiError } from '@crave/api-client';
import { Button, Card, Field } from '@crave/ui';
import { api } from '../api.ts';
import { FakePayment, StripePayment, preloadPaymentForm } from '../components/StripePayment.tsx';
import { onSession } from '../lib/auth.ts';
import { cart, useCart, useOrderLocation } from '../lib/cart.ts';
import { PAYMENT_PENDING } from '../lib/payment.ts';
import type { OrderResponse, PaymentConfig, PaymentSync, Quote, SlotDay } from '../lib/types.ts';
import { useMenu } from './MenuPage.tsx';
import { unpaidOrder } from '../lib/unpaid.ts';

const TIP_PRESETS = [0, 10, 15, 20];
/** Quote errors the customer fixes by changing the cart. */
const FIX_CART_CODES = new Set(['ITEM_UNAVAILABLE', 'PRICE_NOT_SET']);

function trackPath(token: string, confirming = false): string {
  return `/order/${token}${confirming ? '?confirming=1' : ''}`;
}

export function CheckoutPage() {
  const lines = useCart();
  const locationSlug = useOrderLocation();
  const menu = useMenu();
  // Coming back to checkout (a refresh on the payment step) with this cart already placed as an
  // order that is not paid: go to that order's payment sheet instead of starting a second order.
  const [resume] = useState(() => {
    const u = unpaidOrder.get();
    return u && cart.isPlacedAs(u.orderId) ? u : null;
  });
  const nav = useNavigate();
  const units = lines.reduce((s, l) => s + l.quantity, 0);
  const [slotId, setSlotId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [smsUpdates, setSmsUpdates] = useState(false);
  const [phoneError, setPhoneError] = useState<string | undefined>();
  const [tipPct, setTipPct] = useState<number | 'custom'>(15);
  const [customTip, setCustomTip] = useState('');
  const [accept, setAccept] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [placed, setPlaced] = useState<{ order: OrderResponse; token: string } | null>(null);
  /**
   * Idempotency key for POST /orders, tied to the body it was first sent with. Retrying the same body
   * reuses it, so the API resumes that order instead of holding the slot again. A changed body gets a
   * fresh key, and so does IDEMPOTENCY_KEY_REUSED (that checkout was closed). Other errors keep it:
   * before an order exists the key is still unused, and after one exists it must be resumed.
   */
  const idem = useRef<{ key: string; body: string } | null>(null);
  const [signedIn, setSignedIn] = useState(false);
  const [useCredit, setUseCredit] = useState(true);
  const [saveCard, setSaveCard] = useState(false);
  useEffect(() => onSession((s) => setSignedIn(Boolean(s))), []);
  const me = useQuery({
    queryKey: ['me'],
    queryFn: async () =>
      (
        await api.get<{
          email: string;
          first_name: string | null;
          last_name: string | null;
          phone: string | null;
          sms_updates: boolean;
        }>('/me')
      ).data,
    enabled: signedIn,
  });
  useEffect(() => {
    const d = me.data;
    if (!d) return;
    setEmail((v) => v || d.email);
    setName((v) => v || [d.first_name, d.last_name].filter(Boolean).join(' '));
    setPhone((v) => v || d.phone || '');
    // Their account's "Text updates" switch starts the box; changing it here changes the switch.
    if (d.sms_updates) setSmsUpdates(true);
  }, [me.data]);

  const itemsBody = useMemo(
    () =>
      lines.map((l) => ({
        menu_item_id: l.menu_item_id,
        quantity: l.quantity,
        modifier_ids: l.modifier_ids,
      })),
    [lines],
  );
  const quote = useQuery({
    queryKey: ['quote', locationSlug, itemsBody, signedIn, useCredit],
    queryFn: async () =>
      (
        await api.post<Quote>(`/public/locations/${locationSlug}/quote`, {
          items: itemsBody,
          tip_cents: 0,
          use_credit: signedIn && useCredit,
        })
      ).data,
    enabled: lines.length > 0,
  });
  const slots = useQuery({
    queryKey: ['slots', locationSlug, units],
    queryFn: async () =>
      (
        await api.get<{ dates: SlotDay[] }>(
          `/public/locations/${locationSlug}/slots?units=${units}`,
        )
      ).data.dates,
    enabled: units > 0,
    refetchInterval: 30_000,
  });
  const payConfig = useQuery({
    queryKey: ['pay-config'],
    queryFn: async () => (await api.get<PaymentConfig>('/public/payments/config')).data,
  });
  /**
   * The card form is a download of its own, so the menu, item and cart pages never load Stripe.
   * Fetch it while the customer is still filling this page in, so the payment step is ready the
   * moment the order is placed instead of starting a download at the worst possible time.
   */
  const stripeProvider = payConfig.data?.provider === 'stripe';
  useEffect(() => {
    if (stripeProvider) preloadPaymentForm();
  }, [stripeProvider]);

  const t = quote.data?.totals;
  const discount = t?.discount_cents ?? 0;
  const creditApplied = t?.credit_applied_cents ?? 0;
  const food = t ? t.subtotal_cents - discount + t.tax_cents : 0;
  const tipCents =
    tipPct === 'custom'
      ? Math.max(0, Math.round(Number(customTip || 0) * 100))
      : Math.round(((quote.data?.totals.subtotal_cents ?? 0) * tipPct) / 100);
  const total = Math.max(0, food + tipCents - creditApplied);

  const quoteError = quote.isError
    ? quote.error instanceof ApiError
      ? quote.error
      : null
    : undefined;
  const quoteCode = quoteError?.code;
  /** Totals to show. A failed background refetch keeps old data, which may no longer be right. */
  const shown = quote.isError ? undefined : quote.data;
  /** Shown in place of an amount until the total is known. */
  const notYet = quote.isError ? 'Not available' : 'Adding up…';
  const fixCart = quoteCode !== undefined && FIX_CART_CODES.has(quoteCode);

  function keyFor(body: string): string {
    if (!idem.current || idem.current.body !== body)
      idem.current = { key: crypto.randomUUID(), body };
    return idem.current.key;
  }

  const place = useMutation({
    mutationFn: async () => {
      const body = {
        location: locationSlug,
        items: itemsBody,
        slot_id: slotId,
        contact: { name, email, phone: phone || undefined, sms_updates: smsUpdates },
        tip_cents: tipCents,
        accept_policies: accept,
        use_credit: signedIn && useCredit,
        save_payment_method: signedIn && saveCard,
      };
      const bodyKey = JSON.stringify(body);
      const send = async () =>
        (await api.post<OrderResponse>('/orders', body, { idempotencyKey: keyFor(bodyKey) })).data;
      try {
        return await send();
      } catch (e) {
        // The earlier attempt with this key was canceled: start over once with a fresh key.
        if (e instanceof ApiError && e.code === 'IDEMPOTENCY_KEY_REUSED') {
          idem.current = null;
          return await send();
        }
        throw e;
      }
    },
    onSuccess: (o) => {
      setError(null);
      const token = o.tracking_token;
      if (!token) {
        // Keep the cart and the key: trying again resumes this same order.
        setError('We could not open your order just now. Please try again in a moment.');
        return;
      }
      if (o.payment.mode === 'none') {
        cart.clear();
        void nav(trackPath(token));
        return;
      }
      cart.markPlaced(o.order_id);
      // Survives a refresh or a closed tab: every page offers the way back to paying it.
      unpaidOrder.remember({ orderId: o.order_id, token, orderNumber: o.order_number });
      setPlaced({ order: o, token });
    },
    onError: (e) => {
      // Keep the key. IDEMPOTENCY_CONFLICT, PAYMENT_PROVIDER_UNAVAILABLE on a resume, or a lost
      // response can all mean the order already exists, and a fresh key would place it twice.
      setError(e instanceof ApiError ? e.message : 'Could not place the order. Please try again.');
    },
  });

  if (resume && !placed) return <Navigate to={trackPath(resume.token)} replace />;
  if (lines.length === 0 && !placed)
    return (
      <div>
        <p>Your cart is empty.</p>
        <Link to="/">Back to the menu</Link>
      </div>
    );
  const policies = menu.data?.location.policies;

  if (placed) {
    const { order, token } = placed;
    const finish = (r: PaymentSync | null) => {
      if (r?.status === 'PAYMENT_FAILED') {
        // Keep the cart. The tracking page offers another card.
        void nav(trackPath(token));
        return;
      }
      // The provider accepted the payment. If our check has not caught up yet, the tracking page
      // keeps checking.
      cart.clear();
      void nav(trackPath(token, !r || PAYMENT_PENDING.has(r.status)));
    };
    return (
      <div style={{ display: 'grid', gap: 16 }}>
        <h1 style={{ margin: 0 }}>Payment</h1>
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Pickup</span>
            <strong>{order.pickup_label}</strong>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
            <span>Total</span>
            <strong>{formatCents(order.totals.amount_due_cents)}</strong>
          </div>
          <p style={{ margin: '12px 0 0', fontSize: 14, color: 'var(--ink-soft)' }}>
            {order.payment.mode === 'setup_intent'
              ? 'Your card is saved now and charged when you pick up.'
              : 'Your card is authorized now and charged when you pick up. Free cancellation until we start preparing.'}
          </p>
        </Card>
        {payConfig.isLoading ? (
          <p>Loading secure payment…</p>
        ) : order.payment.provider === 'stripe' &&
          payConfig.data?.publishable_key &&
          order.payment.client_secret ? (
          <StripePayment
            publishableKey={payConfig.data.publishable_key}
            clientSecret={order.payment.client_secret}
            orderId={order.order_id}
            mode={order.payment.mode}
            amountCents={order.totals.amount_due_cents}
            email={email}
            returnUrl={`${window.location.origin}${trackPath(token, true)}`}
            onSettled={finish}
          />
        ) : order.payment.provider === 'fake' ? (
          <FakePayment orderId={order.order_id} onSettled={finish} />
        ) : (
          <Card>
            <p style={{ color: 'var(--crave-red)', margin: 0 }}>
              Online payment is not available right now. Your pickup time is held for 10 minutes.
            </p>
          </Card>
        )}
      </div>
    );
  }

  const submitLabel = place.isPending
    ? 'Holding your time…'
    : quote.isError
      ? fixCart
        ? 'Update your cart to continue'
        : quoteCode === 'ORDERING_PAUSED'
          ? 'Ordering is paused'
          : quoteCode === 'ORDERING_DISABLED'
            ? 'Ordering is closed right now'
            : 'Could not get your total'
      : !quote.data
        ? 'Getting your total…'
        : `Continue to payment · ${formatCents(total)}`;

  return (
    <form
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        if (smsUpdates && !isUsMobile(phone)) {
          setPhoneError('To get texts, enter a US mobile number, like (863) 555-0100.');
          return;
        }
        setPhoneError(undefined);
        if (!slotId) {
          setError('Pick a pickup time.');
          return;
        }
        place.mutate();
      }}
      style={{ display: 'grid', gap: 24 }}
    >
      <h1 style={{ margin: 0 }}>Checkout</h1>

      <section style={{ display: 'grid', gap: 12 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>1. Who's picking up</h2>
        <Field
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="name"
          required
        />
        <Field
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
          hint="For your confirmation and receipt. No spam."
        />
        <Field
          label={smsUpdates ? 'Mobile' : 'Mobile (optional)'}
          type="tel"
          value={phone}
          onChange={(e) => {
            setPhone(e.target.value);
            setPhoneError(undefined);
          }}
          autoComplete="tel"
          required={smsUpdates}
          error={phoneError}
          hint={
            smsUpdates
              ? 'Texts about this order go to this number.'
              : 'Only if we need to reach you about this order.'
          }
        />
        <label
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'flex-start',
            minHeight: 'var(--touch)',
            fontSize: 14,
          }}
        >
          <input
            type="checkbox"
            checked={smsUpdates}
            onChange={(e) => {
              setSmsUpdates(e.target.checked);
              setPhoneError(undefined);
            }}
            style={{ width: 20, height: 20, marginTop: 2, flex: 'none' }}
          />
          <span>
            <strong>Text me about this order.</strong> Up to 3 texts: when it is booked, when it is
            ready, and if it is canceled. Message and data rates may apply. Reply STOP to opt out,
            HELP for help.{' '}
            <Link
              to="/texts"
              target="_blank"
              // Inline padding widens the tap area to a thumb without moving the line.
              style={{ color: 'var(--ink)', fontWeight: 600, padding: '13px 4px' }}
            >
              Text terms
            </Link>
          </span>
        </label>
      </section>

      <section style={{ display: 'grid', gap: 12 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>2. Pickup time</h2>
        {slots.isLoading && <p>Finding open times…</p>}
        {slots.data?.length === 0 && (
          <p style={{ color: 'var(--crave-red)' }}>
            No pickup times are open right now for {units} {units === 1 ? 'pizza' : 'pizzas'}. Try
            fewer pizzas or check back soon.
          </p>
        )}
        {slots.data?.map((d) => (
          <div key={d.date}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>{d.label}</div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))',
                gap: 8,
              }}
            >
              {d.slots.map((s) => (
                <button
                  type="button"
                  key={s.id}
                  onClick={() => setSlotId(s.id)}
                  aria-pressed={slotId === s.id}
                  style={{
                    minHeight: 44,
                    padding: '4px 8px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1.15,
                    whiteSpace: 'nowrap',
                    borderRadius: 8,
                    border: `2px solid ${slotId === s.id ? 'var(--crave-red)' : 'var(--line)'}`,
                    background: slotId === s.id ? 'var(--crave-red)' : 'var(--paper)',
                    color: slotId === s.id ? '#fff' : 'var(--ink)',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {s.label.split(' – ')[0]}
                  {s.remaining <= 2 ? (
                    <span style={{ fontSize: 12, fontWeight: 500 }}>{s.remaining} left</span>
                  ) : null}
                </button>
              ))}
            </div>
          </div>
        ))}
      </section>

      <section style={{ display: 'grid', gap: 12 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>3. Tip for the team</h2>
        <p style={{ margin: 0, color: 'var(--ink-soft)', fontSize: 14 }}>
          Completely optional. If you'd like to leave something for the team, you can.
        </p>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(72px, 1fr))',
            gap: 8,
          }}
        >
          {TIP_PRESETS.map((p) => (
            <button
              type="button"
              key={p}
              onClick={() => setTipPct(p)}
              aria-pressed={tipPct === p}
              style={{
                minHeight: 44,
                padding: '0 8px',
                whiteSpace: 'nowrap',
                borderRadius: 8,
                border: `2px solid ${tipPct === p ? 'var(--ink)' : 'var(--line)'}`,
                background: tipPct === p ? 'var(--ink)' : 'var(--paper)',
                color: tipPct === p ? '#fff' : 'var(--ink)',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {p === 0 ? 'No tip' : `${p}%`}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setTipPct('custom')}
            aria-pressed={tipPct === 'custom'}
            style={{
              minHeight: 44,
              padding: '0 8px',
              whiteSpace: 'nowrap',
              borderRadius: 8,
              border: `2px solid ${tipPct === 'custom' ? 'var(--ink)' : 'var(--line)'}`,
              background: 'var(--paper)',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Other
          </button>
          {tipPct === 'custom' && (
            <input
              aria-label="Custom tip in dollars"
              inputMode="decimal"
              value={customTip}
              onChange={(e) => setCustomTip(e.target.value)}
              placeholder="$"
              style={{
                minHeight: 44,
                width: '100%',
                minWidth: 0,
                padding: '0 10px',
                border: '2px solid var(--line)',
                borderRadius: 8,
              }}
            />
          )}
        </div>
      </section>

      {shown?.birthday && (
        <Card style={{ borderColor: 'var(--crave-red)' }}>
          <strong>Happy birthday.</strong>{' '}
          {shown.birthday.item_name ? `Your ${shown.birthday.item_name}` : 'One pizza'} is on us. It
          is already taken off below.
        </Card>
      )}
      {signedIn && (quote.data?.credit_available_cents ?? 0) > 0 && (
        <label
          style={{ display: 'flex', gap: 12, alignItems: 'center', minHeight: 'var(--touch)' }}
        >
          <input
            type="checkbox"
            checked={useCredit}
            onChange={(e) => setUseCredit(e.target.checked)}
            style={{ width: 20, height: 20 }}
          />
          <span>
            Use my Crave Credit ({formatCents(quote.data?.credit_available_cents ?? 0)} available)
          </span>
        </label>
      )}
      {!signedIn && (
        <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-soft)' }}>
          {/* Padding on an inline link widens the tap area without changing the line. */}
          <Link to="/account" style={{ padding: '13px 2px', margin: '0 -2px' }}>
            Sign in
          </Link>{' '}
          to earn Crave Credit and get a birthday pizza.
        </p>
      )}
      {quote.isError && (
        <Card role="alert" style={{ borderColor: 'var(--crave-red)' }}>
          <p style={{ margin: 0 }}>
            {quoteError ? quoteError.message : 'We could not price your cart.'}
          </p>
          <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
            {fixCart ? (
              <Link to="/cart">Edit cart</Link>
            ) : quoteCode !== 'ORDERING_PAUSED' && quoteCode !== 'ORDERING_DISABLED' ? (
              <button
                type="button"
                onClick={() => void quote.refetch()}
                disabled={quote.isFetching}
                style={{
                  background: 'none',
                  border: 0,
                  padding: 0,
                  color: 'var(--crave-red)',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  font: 'inherit',
                }}
              >
                {quote.isFetching ? 'Trying again…' : 'Try again'}
              </button>
            ) : (
              <Link to="/">Back to the menu</Link>
            )}
          </div>
        </Card>
      )}
      <Card>
        <Row label="Food" value={shown ? formatCents(shown.totals.subtotal_cents) : notYet} />
        {shown && discount > 0 && (
          <Row label="Birthday pizza" value={`−${formatCents(discount)}`} />
        )}
        <Row label="Tax" value={shown ? formatCents(shown.totals.tax_cents) : notYet} />
        <Row label="Tip" value={shown || tipPct === 'custom' ? formatCents(tipCents) : notYet} />
        {shown && creditApplied > 0 && (
          <Row label="Crave Credit" value={`−${formatCents(creditApplied)}`} />
        )}
        <Row label="Total" value={shown ? formatCents(total) : notYet} bold />
        <p style={{ margin: '12px 0 0', fontSize: 13, color: 'var(--ink-soft)' }}>
          No service fees. Charged at pickup.
        </p>
      </Card>

      {signedIn && (
        <label
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            minHeight: 'var(--touch)',
            fontSize: 14,
          }}
        >
          <input
            type="checkbox"
            checked={saveCard}
            onChange={(e) => setSaveCard(e.target.checked)}
            style={{ width: 20, height: 20 }}
          />
          <span>Save this card for next time</span>
        </label>
      )}
      <label style={{ display: 'flex', gap: 12, alignItems: 'flex-start', fontSize: 14 }}>
        <input
          type="checkbox"
          checked={accept}
          onChange={(e) => setAccept(e.target.checked)}
          required
          style={{ width: 20, height: 20, marginTop: 2 }}
        />
        <span>
          {policies?.cancel ?? 'Free cancellation until we start preparing your order.'}{' '}
          {policies?.no_show ?? ''}
        </span>
      </label>
      {error && <p style={{ color: 'var(--crave-red)', margin: 0 }}>{error}</p>}
      <Button type="submit" size="lg" disabled={place.isPending || !shown}>
        {submitLabel}
      </Button>
    </form>
  );
}

/** A US number: ten digits (or 1 and ten), area code and exchange not starting with 0 or 1. */
function isUsMobile(typed: string): boolean {
  let d = typed.replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
  return /^[2-9]\d{2}[2-9]\d{6}$/.test(d);
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        fontWeight: bold ? 700 : 400,
        padding: '4px 0',
      }}
    >
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
